import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { MaskedLines } from './Reveal';
import { PORTRAIT } from '@/data';
import { INVESTOR } from '@/constants/testIds';

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '24%']);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid={INVESTOR.hero}
      className="relative min-h-screen radial-glow overflow-hidden"
    >
      <div className="mx-auto max-w-screen-2xl px-6 md:px-12 pt-40 md:pt-48 pb-20">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.1 }}
          className="overline mb-10"
        >
          Investisseur indépendant — Angel &amp; Venture
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <h1
              data-testid={INVESTOR.heroName}
              className="font-display font-light tracking-tight text-foreground text-[3.2rem] leading-[0.98] sm:text-7xl lg:text-[7.5rem]"
            >
              <MaskedLines
                lines={[
                  'J\u2019investis dans',
                  <>les fondateurs qui</>,
                  <>bâtissent la <span className="italic text-primary">décennie</span></>,
                  'qui vient.',
                ]}
              />
            </h1>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.9, ease: [0.19, 1, 0.22, 1] }}
            className="lg:col-span-4"
          >
            <p className="font-body font-light text-base leading-relaxed text-foreground/75 max-w-sm">
              Zukunft Real Estate GmbH accompagne, du premier chèque à la mise à
              l'échelle, une nouvelle génération d'entreprises technologiques en
              Europe et aux États-Unis.
            </p>
            <div className="mt-8 h-px w-full bg-border" />
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Seed &amp; Série A — Ticket $250K–$2M
            </p>
          </motion.div>
        </div>

        {/* Clipped portrait frame */}
        <motion.div
          initial={{ clipPath: 'inset(100% 0% 0% 0%)' }}
          animate={{ clipPath: 'inset(0% 0% 0% 0%)' }}
          transition={{ duration: 1.3, delay: 1.0, ease: [0.76, 0, 0.24, 1] }}
          className="relative mt-20 md:mt-28 h-[46vh] md:h-[64vh] w-full overflow-hidden border border-border"
        >
          <motion.img
            src={PORTRAIT}
            alt="Zukunft Real Estate GmbH"
            style={{ y: imgY, scale: imgScale }}
            className="absolute inset-0 h-[120%] w-full object-cover object-center grayscale contrast-[1.05]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0b0e] via-transparent to-transparent" />
          <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.25em] text-foreground/80">
              Zukunft Real Estate GmbH
            </span>
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.25em] text-foreground/60">
              Tel Aviv · Londres · New York
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
